import DiagnosisStatusBadge from 'components/badge/DiagnosisStatusBadge';
import { DivConst } from 'constants/divConst';

import type { DiagnosisResponseType } from 'types/api/diagnosisResponseType';
import { formatTimeToDate } from 'utils/app';
import { handleScrollToPopupEmbed } from 'utils/content';
import {
  formatPublishTerm,
  getLineProductionUrl,
  getProductionUrl,
  openLineProductionUrl,
  openProductionUrl,
} from 'utils/diagnosis';

import type { JSX } from 'react';

/**
 * 診断作成編集 診断状態表示コンポーネント
 */
export default function WsDiagnosisEditStatusInfo(props: {
  diagnosis: DiagnosisResponseType;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 公開情報の表示可否を返します。
   */
  const _isVisiblePublishInfo = (): boolean => {
    // 掲載(予約)ステータスの場合は表示
    if (props.diagnosis?.diagnosisStatusDiv === DivConst.items.diagnosisStatus.reserved.value) {
      return true;
    }
    // 一度でも掲載を迎えた診断の場合は表示
    if (props.diagnosis?.publishedAt != null) {
      return true;
    }
  };

  /**
   * ポップアップ埋め込みタグの表示可否を返します。
   * WsContentEditPopupEmbed.tsx の埋め込みタグ表示条件と一致させます。
   */
  const _isVisiblePopupEmbed = (): boolean => {
    if (!props.diagnosis?.popupImageKey) {
      return false;
    }
    return _isVisiblePublishInfo();
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {props.diagnosis && (
        <div className="section-header user-detail">
          <div className="card-detail">
            {props.diagnosis.lineFlag ? (
              <>
                <ul className="list-unstyled list-inline">
                  <li className="list-inline-item">
                    <b>診断作成日</b>：{formatTimeToDate(props.diagnosis.createdAt, 'yyyy/MM/dd')}
                  </li>
                  <li className="list-inline-item">
                    <DiagnosisStatusBadge
                      diagnosisStatusDiv={props.diagnosis?.diagnosisStatusDiv}
                    />
                  </li>
                  {_isVisiblePublishInfo() && (
                    <>
                      <li className="list-inline-item">
                        <b>公開期間</b>：
                        {formatPublishTerm({
                          diagnosisStatusDiv: props.diagnosis.diagnosisStatusDiv,
                          publishStartAt: props.diagnosis.publishStartAt,
                          publishEndAt: props.diagnosis.publishEndAt,
                          publishedAt: props.diagnosis.publishedAt,
                          terminatedAt: props.diagnosis.terminatedAt,
                        })}
                      </li>
                      <li className="list-inline-item">
                        <b>検索エンジン</b>：
                        <a>
                          {props.diagnosis.noIndexFlag && (
                            <span className="text-danger">インデックス対象外</span>
                          )}
                          {!props.diagnosis.noIndexFlag && <span>インデックス対象</span>}
                        </a>
                      </li>
                    </>
                  )}
                </ul>
                {_isVisiblePublishInfo() && (
                  <ul className="list-unstyled list-inline">
                    <li className="list-inline-item">
                      <b>公開URL</b>：
                      {props.diagnosis.diagnosisStatusDiv ===
                        DivConst.items.diagnosisStatus.published.value && (
                        <a
                          className="form-profile"
                          onClick={() => openLineProductionUrl(props.diagnosis.lineCd)}
                        >
                          {getLineProductionUrl(props.diagnosis.lineCd)}
                        </a>
                      )}
                      {props.diagnosis.diagnosisStatusDiv !=
                        DivConst.items.diagnosisStatus.published.value && (
                        <span> {getLineProductionUrl(props.diagnosis.lineCd)}</span>
                      )}
                    </li>
                    <li className="list-inline-item">
                      <b>LINE友だち追加後のURL</b>：
                      {props.diagnosis.diagnosisStatusDiv ===
                        DivConst.items.diagnosisStatus.published.value && (
                        <a
                          className="form-profile"
                          onClick={() => openProductionUrl(props.diagnosis.cd)}
                        >
                          {getProductionUrl(props.diagnosis.cd)}
                        </a>
                      )}
                      {props.diagnosis.diagnosisStatusDiv !=
                        DivConst.items.diagnosisStatus.published.value && (
                        <span> {getProductionUrl(props.diagnosis.cd)}</span>
                      )}
                    </li>
                  </ul>
                )}
                {_isVisiblePopupEmbed() && (
                  <ul className="list-unstyled list-inline mt-2">
                    <li className="list-inline-item">
                      <a href="#popup-embed" onClick={handleScrollToPopupEmbed}>
                        ポップアップ埋め込みタグ
                      </a>
                    </li>
                  </ul>
                )}
              </>
            ) : (
              <ul className="list-unstyled list-inline">
                <li className="list-inline-item">
                  <b>診断作成日</b>：{formatTimeToDate(props.diagnosis.createdAt, 'yyyy/MM/dd')}
                </li>
                <li className="list-inline-item">
                  <DiagnosisStatusBadge diagnosisStatusDiv={props.diagnosis?.diagnosisStatusDiv} />
                </li>
                {_isVisiblePublishInfo() && (
                  <>
                    <li className="list-inline-item">
                      <b>公開期間</b>：
                      {formatPublishTerm({
                        diagnosisStatusDiv: props.diagnosis.diagnosisStatusDiv,
                        publishStartAt: props.diagnosis.publishStartAt,
                        publishEndAt: props.diagnosis.publishEndAt,
                        publishedAt: props.diagnosis.publishedAt,
                        terminatedAt: props.diagnosis.terminatedAt,
                      })}
                    </li>
                    <li className="list-inline-item">
                      <b>検索エンジン</b>：
                      <a>
                        {props.diagnosis.noIndexFlag && (
                          <span className="text-danger">インデックス対象外</span>
                        )}
                        {!props.diagnosis.noIndexFlag && <span>インデックス対象</span>}
                      </a>
                    </li>
                    <li className="list-inline-item">
                      <b>公開URL</b>：
                      {props.diagnosis.diagnosisStatusDiv ===
                        DivConst.items.diagnosisStatus.published.value && (
                        <a
                          className="form-profile"
                          onClick={() => openProductionUrl(props.diagnosis.cd)}
                        >
                          {getProductionUrl(props.diagnosis.cd)}
                        </a>
                      )}
                      {props.diagnosis.diagnosisStatusDiv !=
                        DivConst.items.diagnosisStatus.published.value && (
                        <span> {getProductionUrl(props.diagnosis.cd)}</span>
                      )}
                    </li>
                  </>
                )}
                {_isVisiblePopupEmbed() && (
                  <ul className="list-unstyled list-inline mt-2">
                    <li className="list-inline-item">
                      <a href="#popup-embed" onClick={handleScrollToPopupEmbed}>
                        ポップアップ埋め込みタグ
                      </a>
                    </li>
                  </ul>
                )}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}
